import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Activity, 
  Clock, 
  TrendingUp,
  CheckCircle,
  BookOpen,
  Smile,
  Calendar,
  CircleDot,
  RefreshCw,
  FileText,
  Network,
  Download,
  Mail,
  Inbox,
  Sparkles
} from "lucide-react";



const AdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [changelog, setChangelog] = useState(null);
  const [contactRequests, setContactRequests] = useState({ items: [], counts: {} });
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    fetchAdminData();
    fetchChangelog();
    fetchContactRequests();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/stats`);
      setStats(response.data.stats);
      setPrivacyNotice(response.data.privacy_notice);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accesso non autorizzato. Solo gli amministratori possono accedere a questa pagina.");
      } else {
        setError("Errore nel caricamento dei dati.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChangelog = async () => {
    try {
      const r = await api.get(`/admin/methodology/changelog`);
      setChangelog(r.data);
    } catch (_) {
      // 403 for non-clinician → silently skip
      setChangelog(null);
    }
  };

  const fetchContactRequests = async () => {
    try {
      const r = await api.get(`/admin/contact-requests`);
      setContactRequests(r.data || { items: [], counts: {} });
    } catch (_) {
      setContactRequests({ items: [], counts: {} });
    }
  };

  const toggleResolved = async (requestId) => {
    try {
      await api.patch(`/admin/contact-requests/${requestId}/resolve`);
      await fetchContactRequests();
    } catch (err) {
      alert("Non è stato possibile aggiornare lo stato. Riprova.");
    }
  };

  // Opens a clinician-only methodology document in a new tab.
  // The endpoint requires Bearer auth, so we fetch as blob and open via blob URL.
  const openMethodologyDoc = async (kind, { download = false } = {}) => {
    try {
      const response = await api.get(
        `/admin/methodology/${kind}${download ? "?download=1" : ""}`,
        { responseType: "blob" }
      );
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download =
          kind === "md"
            ? "revela_methodology_v2.4.md"
            : "revela_struttura_v2.4.html";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert(
        err.response?.status === 403
          ? "Documento riservato al clinico"
          : "Errore nel caricamento del documento"
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Mai";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return "Mai";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return diffMins <= 1 ? "1 min fa" : `${diffMins} min fa`;
    if (diffHours < 24) return diffHours === 1 ? "1 ora fa" : `${diffHours} ore fa`;
    if (diffDays < 7) return diffDays === 1 ? "1 giorno fa" : `${diffDays} giorni fa`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="border-destructive">
          <CardContent className="p-6 text-center">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="page-title">Console Admin</h1>
          <p className="page-subtitle">Panoramica utenti e utilizzo</p>
        </div>
        <Button variant="outline" onClick={fetchAdminData} className="rounded-sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats-grid">
        <Card className="border border-border shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-mono font-semibold" data-testid="admin-total-users">{stats?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Utenti totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-mono font-semibold" data-testid="admin-active-today">{stats?.active_today ?? 0}</p>
                <p className="text-xs text-muted-foreground">Attivi oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-mono font-semibold" data-testid="admin-active-week">{stats?.active_week ?? 0}</p>
                <p className="text-xs text-muted-foreground">Attivi (7gg)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-mono font-semibold" data-testid="admin-active-month">{stats?.active_month ?? 0}</p>
                <p className="text-xs text-muted-foreground">Attivi (30gg)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-mono font-semibold">{stats?.total_entries || 0}</p>
                <p className="text-xs text-muted-foreground">Entries totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-[11px] text-muted-foreground italic" data-testid="admin-active-criterion">
        Criterio "attivi": utenti distinti con almeno 1 record (check-in, diario, spesa, abitudine,
        umore, bisogno, appuntamento) nella finestra. Non vengono contati login o sessioni.
        I valori &lt; 5 vengono mascherati come "—" per anonimato statistico.
      </p>

      {/* Activity Stats */}
      <Card className="border border-border shadow-sm rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Statistiche per Modulo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-muted rounded-sm">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_expenses || 0}</p>
              <p className="text-xs text-muted-foreground">Spese</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-sm">
              <CheckCircle className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_habits || 0}</p>
              <p className="text-xs text-muted-foreground">Abitudini</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-sm">
              <CircleDot className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_needs || 0}</p>
              <p className="text-xs text-muted-foreground">Bisogni</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-sm">
              <BookOpen className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_diaries || 0}</p>
              <p className="text-xs text-muted-foreground">Diari</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-sm">
              <Smile className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_moods || 0}</p>
              <p className="text-xs text-muted-foreground">Umore</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-sm">
              <Calendar className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-mono font-semibold">{stats?.total_appointments || 0}</p>
              <p className="text-xs text-muted-foreground">Appuntamenti</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GDPR Privacy Notice */}
      {privacyNotice && (
        <Card className="border-2 border-amber-200 bg-amber-50 rounded-sm">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <Activity className="w-4 h-4 text-amber-700" />
            </div>
            <div className="text-xs text-amber-900 leading-relaxed">
              <p className="font-semibold mb-1">Modalità privacy attiva (GDPR + CNOP)</p>
              <p>{privacyNotice}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Methodology Documents (clinician-only) */}
      <Card className="border-2 border-indigo-200 bg-indigo-50/40 rounded-sm" data-testid="methodology-docs-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-700" />
            Documenti metodologici (privati)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-indigo-900/80 leading-relaxed">
            Mappa completa di Revela v2.4: 21 dimensioni, 16 coppie inter-variabili, 15 costrutti
            indiretti, 7 indicatori derivati, 6 pattern temporali, 3 confronti normativi. Visibile
            <strong> solo al clinico</strong> — beta tester e pubblico non hanno accesso.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => openMethodologyDoc("md")}
              data-testid="open-methodology-md"
            >
              <FileText className="w-4 h-4 mr-2" />
              Mappa metodologica (MD)
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => openMethodologyDoc("diagram")}
              data-testid="open-methodology-diagram"
            >
              <Network className="w-4 h-4 mr-2" />
              Struttura logica (diagramma)
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-sm text-indigo-700"
              onClick={() => openMethodologyDoc("md", { download: true })}
              data-testid="download-methodology-md"
              title="Scarica la versione Markdown"
            >
              <Download className="w-4 h-4 mr-1" />
              Scarica MD
            </Button>
          </div>

          {/* Changelog versioni — clinician-only */}
          {changelog?.versions?.length > 0 && (
            <div className="pt-2 border-t border-indigo-200/60 mt-3" data-testid="methodology-changelog">
              <p className="text-xs font-semibold text-indigo-900 mb-2">
                Changelog versioni
                {changelog.current && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-mono text-[10px]">
                    attuale: {changelog.current}
                  </span>
                )}
              </p>
              <ul className="space-y-2">
                {changelog.versions.map((v) => (
                  <li
                    key={v.id + v.date}
                    className="text-xs text-indigo-950/80 leading-relaxed border-l-2 border-indigo-300 pl-3"
                  >
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono font-semibold text-indigo-700">{v.id}</span>
                      <span className="text-indigo-700/60">·</span>
                      <span className="text-indigo-700/80">{v.date}</span>
                    </div>
                    <div className="font-medium mt-0.5">{v.title}</div>
                    {v.highlights?.length > 0 && (
                      <ul className="list-disc list-inside text-indigo-950/70 mt-1 space-y-0.5">
                        {v.highlights.map((h, i) => (
                          <li key={i}>{h}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Landing page contact requests (beta testers + scientific partners) */}
      <Card
        className="border border-border shadow-sm rounded-sm"
        data-testid="landing-requests-card"
      >
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Inbox className="w-5 h-5 text-slate-700" />
              Richieste dalla landing page
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Persone che hanno compilato i form <em>“Diventa beta tester”</em> o{" "}
              <em>“Partner scientifico”</em>. Al momento non c'è un servizio email
              collegato: le richieste vengono salvate qui.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0"
            onClick={fetchContactRequests}
            data-testid="refresh-landing-requests"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Aggiorna
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-xl font-mono font-semibold" data-testid="landing-count-total">
                {contactRequests.counts?.total ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">Richieste totali</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-sm">
              <p className="text-xl font-mono font-semibold text-emerald-800" data-testid="landing-count-beta">
                {contactRequests.counts?.beta ?? 0}
              </p>
              <p className="text-xs text-emerald-900/70">Beta tester</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-sm">
              <p className="text-xl font-mono font-semibold text-indigo-800" data-testid="landing-count-partner">
                {contactRequests.counts?.partner ?? 0}
              </p>
              <p className="text-xs text-indigo-900/70">Partner scientifici</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-sm">
              <p className="text-xl font-mono font-semibold text-amber-800" data-testid="landing-count-unresolved">
                {contactRequests.counts?.unresolved ?? 0}
              </p>
              <p className="text-xs text-amber-900/70">Da gestire</p>
            </div>
          </div>

          {/* Toggle: show resolved */}
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Ordinate dalla più recente. Clicca l'email per rispondere direttamente.
            </p>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                className="rounded-sm"
                data-testid="toggle-show-resolved"
              />
              Mostra anche quelle già gestite
            </label>
          </div>

          {/* List */}
          {contactRequests.items?.length === 0 ? (
            <div
              className="text-center py-8 text-sm text-muted-foreground italic"
              data-testid="landing-requests-empty"
            >
              Nessuna richiesta ricevuta finora.
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-sm border border-border overflow-hidden">
              {contactRequests.items
                .filter((it) => showResolved || !it.resolved)
                .map((it) => (
                  <li
                    key={it.id}
                    className={`p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${
                      it.resolved ? "bg-muted/40" : "bg-background"
                    }`}
                    data-testid={`landing-request-${it.id}`}
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${
                            it.type === "beta"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-indigo-100 text-indigo-800"
                          }`}
                        >
                          {it.type === "beta" ? "Beta tester" : "Partner scientifico"}
                        </span>
                        <span className="font-medium text-sm truncate">{it.name}</span>
                        {it.resolved && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700">
                            gestita
                          </span>
                        )}
                      </div>
                      <a
                        href={`mailto:${it.email}?subject=${encodeURIComponent(
                          it.type === "beta"
                            ? "Revela — accesso beta tester"
                            : "Revela — collaborazione scientifica"
                        )}`}
                        className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                        data-testid={`landing-request-email-${it.id}`}
                      >
                        <Mail className="w-3 h-3" />
                        {it.email}
                      </a>
                      {it.message && (
                        <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap leading-relaxed">
                          "{it.message}"
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {getTimeSince(it.created_at)}
                        {it.resolved && it.resolved_at && (
                          <> · gestita il {formatDate(it.resolved_at)}</>
                        )}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <Button
                        variant={it.resolved ? "ghost" : "outline"}
                        size="sm"
                        className="rounded-sm"
                        onClick={() => toggleResolved(it.id)}
                        data-testid={`landing-request-toggle-${it.id}`}
                      >
                        <CheckCircle
                          className={`w-3.5 h-3.5 mr-1.5 ${
                            it.resolved ? "text-emerald-600" : ""
                          }`}
                        />
                        {it.resolved ? "Riapri" : "Segna gestita"}
                      </Button>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Users Table — REMOVED for GDPR compliance (2026-05-21).
          Admin sees only aggregate stats; individual user data is never exposed. */}
    </div>
  );
};

export default AdminPage;
